<template>
  <div id="extrusion-resource-list" class="operator-page">
    <!-- Operator Header -->
    <div class="header">
      <div class="header__top d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <span class="header__back mr-1" @click="goBackToOperatorMenu" title="Voltar">
            <BackIcon />
          </span>
          <span class="header__title">Prensas de Extrusão</span>
        </div>

        <div class="header__actions d-flex align-items-center">
          <button class="btn-operator-secondary mr-1" @click="fetchResources" :disabled="loading">
            <feather-icon icon="RefreshCwIcon" :class="{ 'spin': loading }" class="mr-50" />
            Atualizar
          </button>

          <button class="btn-operator-primary" @click="goToFurnaces">
            <feather-icon icon="ThermometerIcon" class="mr-50" />
            Fornos
          </button>
        </div>
      </div>
    </div>

    <div class="operator-body">
      <!-- Search & Filters Bar -->
      <div class="filter-card mb-2">
        <b-row class="align-items-center">
          <b-col cols="12" md="6" lg="5" class="mb-1 mb-md-0">
            <div class="search-input-wrapper">
              <span class="search-icon">
                <feather-icon icon="SearchIcon" size="18" />
              </span>
              <input
                type="text"
                v-model="searchQuery"
                class="operator-input"
                placeholder="Buscar por código, nome da prensa, OP ou status..."
              />
            </div>
          </b-col>

          <b-col cols="12" md="6" lg="4" class="mb-1 mb-md-0">
            <div class="d-flex align-items-center">
              <label for="status-filter" class="filter-label mb-0 mr-1">Status:</label>
              <select id="status-filter" v-model="selectedStatus" class="operator-select">
                <option value="">Todas as Prensas</option>
                <option value="active">Em Produção (Todas)</option>
                <option value="extruding">Extrudando</option>
                <option value="cutting">Em Corte</option>
                <option value="stopped">Em Parada</option>
                <option value="idle">Disponíveis</option>
              </select>
            </div>
          </b-col>

          <b-col cols="12" md="12" lg="3" class="text-lg-right mt-1 mt-lg-0">
            <span class="count-info">
              Exibindo <strong>{{ filteredResources.length }}</strong> de <strong>{{ resources.length }}</strong> prensas
            </span>
          </b-col>
        </b-row>
      </div>

      <!-- Loading State -->
      <div v-if="loading" class="loading-state text-center py-5">
        <b-spinner variant="warning" label="Carregando..." class="mb-1" />
        <p class="loading-text">Carregando recursos de extrusão...</p>
      </div>

      <!-- Empty State -->
      <div v-else-if="filteredResources.length === 0" class="empty-state text-center py-5">
        <feather-icon icon="InboxIcon" size="48" class="empty-icon mb-1" />
        <h5 class="empty-title">Nenhuma Prensa Encontrada</h5>
        <p class="empty-desc" v-if="searchQuery || selectedStatus">
          Nenhum recurso atende aos filtros pesquisados. Tente ajustar os parâmetros.
        </p>
        <p class="empty-desc" v-else>
          Nenhum recurso de extrusão disponível para o seu centro de trabalho nesta unidade.
        </p>
      </div>

      <!-- Resources Cards Grid -->
      <b-row v-else class="cards-grid">
        <b-col
          v-for="item in filteredResources"
          :key="item.resourceId"
          cols="12"
          md="6"
          lg="4"
          xl="3"
          class="mb-2 d-flex align-items-stretch"
        >
          <div
            class="operator-resource-card cursor-pointer"
            :class="{
              'has-active-op': item.activeOrdersCount > 0,
              'is-stopped': item.activeOrdersCount > 0 && item.isStopped,
              'is-extruding': item.activeOrdersCount > 0 && !item.isStopped && item.extrudingOrdersCount > 0,
              'is-cutting': item.activeOrdersCount > 0 && !item.isStopped && item.extrudingOrdersCount === 0 && item.cuttingOrdersCount > 0
            }"
            :style="getCardBorderLeftStyle(item)"
            @click="openOrderSelection(item)"
          >
            <!-- Card Header -->
            <div class="card-top d-flex justify-content-between align-items-center">
              <div class="code-block">
                <span class="resource-code">{{ item.code }}</span>
                <span class="workcenter-name">{{ item.workcenterName || 'Extrusão' }}</span>
              </div>
              <span
                v-if="item.activeOrdersCount > 0"
                class="status-badge"
                :class="{
                  'status-parada': item.isStopped,
                  'status-extrudando': !item.isStopped && item.extrudingOrdersCount > 0,
                  'status-corte': !item.isStopped && item.extrudingOrdersCount === 0 && item.cuttingOrdersCount > 0,
                  'status-em-operacao': !item.isStopped && item.extrudingOrdersCount === 0 && item.cuttingOrdersCount === 0
                }"
                :style="getStatusBadgeStyle(item)"
              >
                <span
                  class="status-dot"
                  :style="{ backgroundColor: getStatusDotColor(item) }"
                ></span>
                {{ getStatusBadgeLabel(item) }}
              </span>
              <span v-else class="status-badge status-nao-iniciado">
                <span class="status-dot"></span>
                Disponível
              </span>
            </div>

            <!-- Resource Name -->
            <div class="resource-title mt-1 mb-1">
              {{ item.name }}
            </div>

            <!-- Multi-Order Info Box -->
            <div class="op-box mb-2">
              <div class="d-flex justify-content-between align-items-center mb-50">
                <span class="op-label">ORDENS DISPONÍVEIS</span>
                <span class="badge-op-count font-weight-bold">{{ item.availableOrdersCount || 0 }}</span>
              </div>
              
              <!-- Se houver ordem ativa e estiver em parada -->
              <div v-if="item.activeOrdersCount > 0 && item.isStopped" class="d-flex align-items-center text-danger font-weight-bold small mt-50">
                <feather-icon icon="AlertTriangleIcon" size="13" class="mr-25 flex-shrink-0" />
                <span class="text-truncate" :title="`${item.currentStatusName || 'Parada'} na OP #${item.activeOrderCode}`">
                  {{ item.currentStatusName || 'Parada' }} na OP {{ item.activeOrderCode ? `#${item.activeOrderCode}` : 'ativa' }}
                </span>
              </div>

              <!-- Se houver ordem ativa e estiver produtiva -->
              <div
                v-else-if="item.activeOrdersCount > 0"
                class="d-flex align-items-center font-weight-bold small mt-50"
                :style="{ color: item.extrudingOrdersCount > 0 ? '#974900' : (item.cuttingOrdersCount > 0 ? '#7367f0' : (item.currentStatusColor || '#28c76f')) }"
              >
                <feather-icon icon="ActivityIcon" size="13" class="mr-25 flex-shrink-0" />
                <span class="text-truncate" :title="getActiveOrderTooltip(item)">
                  {{ getActiveOrderSummary(item) }}
                </span>
              </div>

              <div v-else class="text-muted small mt-50">
                Nenhum apontamento em andamento
              </div>
            </div>

            <!-- Card Footer Action -->
            <div class="card-actions mt-auto pt-1">
              <button
                class="btn-operator-action"
                @click.stop="openOrderSelection(item)"
              >
                <feather-icon icon="LayersIcon" class="mr-50" />
                Selecionar Ordens / Iniciar
              </button>
            </div>
          </div>
        </b-col>
      </b-row>
    </div>

    <!-- Modal de Seleção de Ordens e Materiais -->
    <SelectExtrusionOrderModal
      v-model="showOrderModal"
      :resource="selectedResourceForModal"
      :site-id="siteId"
    />
  </div>
</template>

<script>
import { BRow, BCol, BSpinner } from 'bootstrap-vue';
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue';
import { getExtrusionResources } from './extrusionApi';
import BackIcon from '@/assets/images/icons/back.svg';
import SelectExtrusionOrderModal from './components/SelectExtrusionOrderModal.vue';

export default {
  name: 'ExtrusionResourceList',
  components: {
    BRow,
    BCol,
    BSpinner,
    BackIcon,
    SelectExtrusionOrderModal,
    ToastificationContent
  },
  data() {
    return {
      resources: [],
      loading: false,
      searchQuery: '',
      selectedStatus: '',
      showOrderModal: false,
      selectedResourceForModal: null
    };
  },
  computed: {
    siteId() {
      const cookieSite = this.$cookies?.get('userInfo')?.currentSite;
      if (cookieSite) {
        if (typeof cookieSite === 'object' && cookieSite.id) return cookieSite.id;
        if (typeof cookieSite === 'number') return cookieSite;
        if (!isNaN(cookieSite)) return parseInt(cookieSite, 10);
      }
      const storedSite = localStorage.getItem('siteId') || localStorage.getItem('currentSite');
      if (storedSite) {
        try {
          const parsed = JSON.parse(storedSite);
          if (parsed && parsed.id) return parsed.id;
          if (typeof parsed === 'number') return parsed;
        } catch (e) {
          if (!isNaN(storedSite)) return parseInt(storedSite, 10);
        }
      }
      return 1;
    },
    filteredResources() {
      if (!Array.isArray(this.resources)) return [];
      return this.resources.filter(item => {
        if (this.selectedStatus === 'active') {
          if (item.activeOrdersCount <= 0 || item.isStopped) return false;
        }
        if (this.selectedStatus === 'extruding') {
          if (item.extrudingOrdersCount <= 0 || item.isStopped) return false;
        }
        if (this.selectedStatus === 'cutting') {
          if (item.cuttingOrdersCount <= 0 || item.isStopped) return false;
        }
        if (this.selectedStatus === 'stopped') {
          if (item.activeOrdersCount <= 0 || !item.isStopped) return false;
        }
        if (this.selectedStatus === 'idle') {
          if (item.activeOrdersCount > 0) return false;
        }
        if (this.searchQuery) {
          const q = this.searchQuery.toLowerCase();
          const matchCode = item.code && item.code.toLowerCase().includes(q);
          const matchName = item.name && item.name.toLowerCase().includes(q);
          const matchWorkcenter = item.workcenterName && item.workcenterName.toLowerCase().includes(q);
          const matchOp = item.activeOrderCode && String(item.activeOrderCode).toLowerCase().includes(q);
          const matchStatus = item.currentStatusName && item.currentStatusName.toLowerCase().includes(q);
          const matchMaterial = item.activeMaterialName && item.activeMaterialName.toLowerCase().includes(q);
          const matchStage = (item.extrudingOrdersCount > 0 && 'extrudando'.includes(q)) ||
                             (item.cuttingOrdersCount > 0 && ('corte'.includes(q) || 'em corte'.includes(q)));
          return matchCode || matchName || matchWorkcenter || matchOp || matchStatus || matchMaterial || matchStage;
        }
        return true;
      });
    }
  },
  created() {
    this.fetchResources();
  },
  methods: {
    getStatusBadgeLabel(item) {
      if (!item) return '';
      if (item.isStopped) {
        return item.currentStatusName || 'Parada';
      }
      if (item.extrudingOrdersCount > 0) {
        return `Extrudando (${item.extrudingOrdersCount})`;
      }
      if (item.cuttingOrdersCount > 0) {
        return `Em corte (${item.cuttingOrdersCount})`;
      }
      return item.currentStatusName || (item.activeOrdersCount > 0 ? `Em Produção (${item.activeOrdersCount})` : 'Disponível');
    },
    getStatusDotColor(item) {
      if (!item) return '#808080';
      if (item.isStopped) {
        return item.currentStatusColor || '#ea5455';
      }
      if (item.extrudingOrdersCount > 0) {
        return '#974900';
      }
      if (item.cuttingOrdersCount > 0) {
        return '#7367f0';
      }
      return item.currentStatusColor || '#28c76f';
    },
    getStatusBadgeStyle(item) {
      if (!item) return {};
      if (item.isStopped) {
        return this.generateBadgeColors(item.currentStatusColor || '#ea5455');
      }
      if (item.extrudingOrdersCount > 0) {
        return {
          backgroundColor: '#fae8d8',
          color: '#974900',
          borderColor: '#ffd2b3'
        };
      }
      if (item.cuttingOrdersCount > 0) {
        return {
          backgroundColor: '#ebe9fe',
          color: '#7367f0',
          borderColor: '#d1cbfb'
        };
      }
      return this.generateBadgeColors(item.currentStatusColor || '#28c76f');
    },
    generateBadgeColors(rawColor) {
      if (!rawColor) return {};
      const trimmed = String(rawColor).trim();
      let bg = `${trimmed}18`;
      if (trimmed.startsWith('#')) {
        let hex = trimmed.replace('#', '');
        if (hex.length === 3) hex = hex.split('').map(c => c + c).join('');
        if (hex.length >= 6) {
          const r = parseInt(hex.substring(0, 2), 16);
          const g = parseInt(hex.substring(2, 4), 16);
          const b = parseInt(hex.substring(4, 6), 16);
          if (!isNaN(r) && !isNaN(g) && !isNaN(b)) {
            bg = `rgba(${r}, ${g}, ${b}, 0.12)`;
          }
        }
      }
      return {
        backgroundColor: bg,
        color: trimmed,
        borderColor: trimmed
      };
    },
    getCardBorderLeftStyle(item) {
      if (!item || item.activeOrdersCount <= 0) return {};
      if (item.isStopped) {
        return { borderLeft: '5px solid #ea5455' };
      }
      if (item.extrudingOrdersCount > 0) {
        return { borderLeft: '5px solid #974900' };
      }
      if (item.cuttingOrdersCount > 0) {
        return { borderLeft: '5px solid #7367f0' };
      }
      const color = item.currentStatusColor || '#28c76f';
      return { borderLeft: `5px solid ${color}` };
    },
    getActiveOrderSummary(item) {
      if (!item) return '';
      const opText = item.activeOrderCode ? `OP #${item.activeOrderCode}` : 'OP ativa';
      if (item.extrudingOrdersCount > 0 && item.cuttingOrdersCount > 0) {
        return `${opText} (Extrudando) • ${item.cuttingOrdersCount} em corte`;
      }
      if (item.extrudingOrdersCount > 0) {
        return `${opText} (Extrudando)`;
      }
      if (item.cuttingOrdersCount > 0) {
        return item.cuttingOrdersCount > 1
          ? `${opText} (Em corte) • +${item.cuttingOrdersCount - 1} em corte`
          : `${opText} (Em corte)`;
      }
      return `${opText} (${item.currentStatusName || 'Em Produção'})`;
    },
    getActiveOrderTooltip(item) {
      if (!item) return '';
      const summary = this.getActiveOrderSummary(item);
      return item.activeMaterialName ? `${summary} - ${item.activeMaterialName}` : summary;
    },
    async fetchResources() {
      this.loading = true;
      try {
        const response = await getExtrusionResources(this.siteId);
        const resData = response.data;
        let list = [];
        if (Array.isArray(resData)) {
          list = resData;
        } else if (resData && Array.isArray(resData.data)) {
          list = resData.data;
        }
        
        this.resources = list.map(item => ({
          resourceId: item.resourceId,
          code: item.resourceCode,
          name: item.resourceName,
          workcenterName: item.workcenterName || 'Extrusão',
          availableOrdersCount: item.availableOrdersCount || 0,
          activeOrdersCount: item.activeOrdersCount || 0,
          extrudingOrdersCount: item.extrudingOrdersCount || 0,
          cuttingOrdersCount: item.cuttingOrdersCount || 0,
          currentStatusId: item.currentStatusId,
          currentStatusName: item.currentStatusName || (item.activeOrdersCount > 0 ? 'Em Produção' : 'Disponível'),
          currentStatusColor: item.currentStatusColor || (item.activeOrdersCount > 0 ? '#28C76F' : '#808080'),
          isStopped: !!item.isStopped,
          statusClassificationId: item.statusClassificationId,
          activeOrderId: item.activeOrderId,
          activeOrderCode: item.activeOrderCode,
          activeMaterialName: item.activeMaterialName,
          lastUpdatedAt: item.lastUpdatedAt
        }));
      } catch (error) {
        console.error('Erro ao carregar recursos de extrusão:', error);
        this.resources = [];
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Erro de Conexão',
            icon: 'AlertTriangleIcon',
            text: 'Não foi possível carregar os recursos de extrusão.',
            variant: 'danger'
          }
        });
      } finally {
        this.loading = false;
      }
    },
    openOrderSelection(item) {
      this.selectedResourceForModal = item;
      this.showOrderModal = true;
    },
    goToFurnaces() {
      const prefix = this.$route.params.prefix || 'sfc';
      this.$router.push({
        name: 'operator-extrusion-furnaces',
        params: { prefix }
      });
    },
    goBackToOperatorMenu() {
      const prefix = this.$route.params.prefix || 'sfc';
      this.$router.push({
        name: 'operator-resources-menu',
        params: { prefix }
      });
    }
  }
};
</script>

<style lang="scss">
.app-content-operator:has(#extrusion-resource-list) {
  padding: 0 !important;
}

#extrusion-resource-list {
  min-height: calc(100vh - 110px);
  color: #4c4541;

  .header {
    background: #fff;
    border-top: 1px solid #cfc4be;
    border-bottom: 1px solid #cfc4be;
    box-shadow: 0 8px 30px rgba(58, 46, 38, 0.1);

    .header__top {
      padding: 15.5px 29px;

      .header__back {
        display: flex;
        align-items: center;
        cursor: pointer;
        transition: transform 0.2s;

        &:hover {
          transform: translateX(-3px);
        }
      }

      .header__title {
        font-family: "Raleway", sans-serif;
        font-size: 22px;
        font-weight: 600;
        line-height: 30px;
        color: #4c4541;
      }
    }
  }

  .operator-body {
    padding: 24px 29px;
  }

  /* Filter Card */
  .filter-card {
    background: #fff;
    border: 1px solid #cfc4be;
    border-radius: 8px;
    padding: 16px 20px;
    box-shadow: 0 4px 18px rgba(58, 46, 38, 0.06);

    .search-input-wrapper {
      position: relative;
      display: flex;
      align-items: center;

      .search-icon {
        position: absolute;
        left: 14px;
        color: #974900;
        display: flex;
        align-items: center;
      }

      .operator-input {
        width: 100%;
        height: 42px;
        padding: 8px 14px 8px 42px;
        border: 1px solid #cfc4be;
        border-radius: 6px;
        background: #fafafa;
        color: #4c4541;
        font-size: 14px;
        outline: none;
        transition: border-color 0.2s, background 0.2s;

        &::placeholder {
          color: #998f8a;
        }

        &:focus {
          border-color: #974900;
          background: #fff;
          box-shadow: 0 0 0 3px rgba(151, 73, 0, 0.1);
        }
      }
    }

    .filter-label {
      font-family: "Raleway", sans-serif;
      font-weight: 600;
      font-size: 14px;
      color: #4c4541;
    }

    .operator-select {
      height: 42px;
      padding: 8px 14px;
      border: 1px solid #cfc4be;
      border-radius: 6px;
      background: #fafafa;
      color: #4c4541;
      font-size: 14px;
      outline: none;

      &:focus {
        border-color: #974900;
        box-shadow: 0 0 0 3px rgba(151, 73, 0, 0.1);
      }
    }

    .count-info {
      font-size: 13px;
      color: #998f8a;

      strong {
        color: #4c4541;
      }
    }
  }

  /* Operator Buttons */
  .btn-operator-primary {
    height: 40px;
    padding: 0 20px;
    background: #974900;
    border: 1px solid #974900;
    border-radius: 6px;
    color: #fff;
    font-family: "Raleway", sans-serif;
    font-size: 14px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s;

    &:hover {
      background: #7a3b00;
      border-color: #7a3b00;
    }
  }

  .btn-operator-secondary {
    height: 40px;
    padding: 0 16px;
    background: #fafafa;
    border: 1px solid #cfc4be;
    border-radius: 6px;
    color: #4c4541;
    font-family: "Raleway", sans-serif;
    font-size: 14px;
    font-weight: 600;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s, color 0.2s;

    &:hover {
      background: #ffede2;
      border-color: #974900;
      color: #974900;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
    }
  }

  /* Cards Grid */
  .cards-grid {
    margin-right: -8px;
    margin-left: -8px;

    > [class*="col-"] {
      padding-right: 8px;
      padding-left: 8px;
    }
  }

  /* Resource Card Operator Style */
  .operator-resource-card {
    width: 100%;
    background: #fafafa;
    border: 1px solid #cfc4be;
    border-radius: 8px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 18px rgba(58, 46, 38, 0.08);
    transition: transform 0.2s, border-color 0.2s, background 0.2s, box-shadow 0.2s;

    &:hover {
      border-color: #974900;
      background: #ffede2;
      transform: translateY(-2px);
      box-shadow: 0 8px 24px rgba(58, 46, 38, 0.14);
    }

    &.has-active-op {
      border-left: 5px solid #974900;
    }

    &.is-extruding {
      border-left: 5px solid #974900 !important;
    }

    &.is-cutting {
      border-left: 5px solid #7367f0 !important;
    }

    &.is-stopped {
      border-left: 5px solid #ea5455 !important;
    }

    &.is-setup {
      border-left: 5px solid #ff9f43 !important;
    }

    .card-top {
      .code-block {
        display: flex;
        flex-direction: column;

        .resource-code {
          font-family: "Raleway", sans-serif;
          font-size: 20px;
          font-weight: 700;
          color: #4c4541;
          line-height: 1.2;
        }

        .workcenter-name {
          font-size: 12px;
          color: #998f8a;
          margin-top: 2px;
        }
      }
    }

    .status-badge {
      display: inline-flex;
      align-items: center;
      padding: 4px 10px;
      border-radius: 12px;
      font-size: 12px;
      font-weight: 600;

      .status-dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        margin-right: 6px;
      }

      &.status-extrudando {
        background: #fae8d8;
        color: #974900;
        border: 1px solid #ffd2b3;
        .status-dot { background: #974900; }
      }

      &.status-corte {
        background: #ebe9fe;
        color: #7367f0;
        border: 1px solid #d1cbfb;
        .status-dot { background: #7367f0; }
      }

      &.status-em-operacao {
        background: #e8fadf;
        color: #28c76f;
        border: 1px solid #28c76f;
        .status-dot { background: #28c76f; }
      }

      &.status-setup {
        background: #fff5e5;
        color: #ff9f43;
        border: 1px solid #ff9f43;
        .status-dot { background: #ff9f43; }
      }

      &.status-parada,
      &.status-parado {
        background: #fceaea;
        color: #ea5455;
        border: 1px solid #ea5455;
        .status-dot { background: #ea5455; }
      }

      &.status-nao-iniciado {
        background: #f3f2f1;
        color: #82868b;
        border: 1px solid #82868b;
        .status-dot { background: #82868b; }
      }
    }

    .resource-title {
      font-family: "Raleway", sans-serif;
      font-size: 15px;
      font-weight: 600;
      color: #4c4541;
      min-height: 42px;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      overflow: hidden;
    }

    .op-box {
      background: #fff;
      border: 1px solid #cfc4be;
      border-radius: 8px;
      padding: 12px 14px;

      .op-label {
        font-size: 11px;
        font-weight: 700;
        color: #7e7570;
        letter-spacing: 0.5px;
      }

      .badge-op-count {
        background: #ffede2;
        color: #974900;
        border: 1px solid #ffd2b3;
        border-radius: 6px;
        padding: 2px 8px;
        font-size: 13px;
      }
    }

    .btn-operator-action {
      width: 100%;
      height: 42px;
      background: #974900;
      border: 1px solid #974900;
      border-radius: 6px;
      color: #fff;
      font-family: "Raleway", sans-serif;
      font-size: 14px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: pointer;
      transition: background 0.2s;

      &:hover {
        background: #7a3b00;
      }
    }

    .btn-operator-disabled {
      width: 100%;
      height: 42px;
      background: #e6e1de;
      border: 1px solid #cfc4be;
      border-radius: 6px;
      color: #998f8a;
      font-family: "Raleway", sans-serif;
      font-size: 14px;
      font-weight: 600;
      display: flex;
      align-items: center;
      justify-content: center;
      cursor: not-allowed;
    }
  }

  .spin {
    animation: spin 1s linear infinite;
  }
}

@keyframes spin {
  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 768px) {
  #extrusion-resource-list {
    padding-top: 49px;

    .header {
      .header__top {
        padding: 12px 16px;
        .header__title {
          font-size: 18px;
        }
      }
    }

    .operator-body {
      padding: 16px;
    }
  }
}
</style>
